import { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import API from "../axios";
import { useNavigate, useParams } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";

import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const UpdateExpectedResult = () => {
    const { id } = useParams();
    const naviate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [name, setName] = useState("");
    const [key, setKey] = useState("");
    const [newSubCategory, setNewSubCategory] = useState("");
    const [newValue, setNewValue] = useState("");

    const [currentSubCategory, setCurrentSubCategory] = useState("");
    const [currentField, setCurrentField] = useState("");

    const [rowInputs, setRowInputs] = useState<
        Record<string, { key: string; type: string; exampleText: string }>
    >({});

    const [data, setData]: any = useState({});

    // Add state to track which input is being edited
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [tempKeyValue, setTempKeyValue] = useState("");

    // For the drag and drop sorting
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        const subCategory = active.data.current.subCategory;

        reorderObjectByKeys(subCategory, data[subCategory], active.id, over.id);
    };

    function reorderObjectByKeys(
        subCategory: string,
        obj: any,
        activeKey: string,
        targetKey: string,
    ) {
        const entries = Object.entries(obj);
        const activeIndex = entries.findIndex(([key]) => key === activeKey);
        const targetIndex = entries.findIndex(([key]) => key === targetKey);

        if (activeIndex === -1 || targetIndex === -1) return obj;

        // Remove the active item
        const [movedItem] = entries.splice(activeIndex, 1);

        // Insert it at the new position
        entries.splice(targetIndex, 0, movedItem);

        const sorted = Object.fromEntries(entries);

        data[subCategory] = sorted;
        setData({
            ...data,
        });
    }

    const handleAddNewSubCategory = () => {
        setData({
            ...data, // spread existing data
            [newSubCategory]: {}, // add the new subcategory
        });
    };

    const handleDeleteRow = (subCategory: string, key: string) => {
        const updatedData = { ...data };

        if (updatedData[subCategory]) {
            delete updatedData[subCategory][key];
        }

        setData(updatedData);
    };

    const handleDeleteItem = (
        subCategory: string,
        key: string,
        index: number,
    ) => {
        const updatedData = { ...data };

        if (updatedData[subCategory]) {
            updatedData[subCategory][key].splice(index, 1);
        }

        setData(updatedData);
    };

    const handleChangeValue = (
        subCategory: string,
        key: string,
        value: string,
    ) => {
        console.log("sub category:", subCategory);
        console.log("key:", key);
        console.log("value: ", value);

        const updatedData = { ...data };

        if (updatedData[subCategory] && updatedData[subCategory][key]) {
            updatedData[subCategory][key] = value;
            setData(updatedData);
        }
    };

    const handleChangeKey = (
        subCategory: string,
        oldKey: string,
        newKey: string,
    ) => {
        const updatedData = { ...data };

        if (updatedData[subCategory] && updatedData[subCategory][oldKey]) {
            const value = updatedData[subCategory][oldKey];

            // Prevent overwriting if newKey already exists and it's different from oldKey
            if (newKey !== oldKey && updatedData[subCategory][newKey]) {
                return;
            }

            // Only update if the key actually changed
            if (newKey !== oldKey) {
                // Create new object with preserved order
                const newSubCategory: any = {};

                Object.keys(updatedData[subCategory]).forEach((key) => {
                    if (key === oldKey) {
                        newSubCategory[newKey] = value;
                    } else {
                        newSubCategory[key] = updatedData[subCategory][key];
                    }
                });

                updatedData[subCategory] = newSubCategory;
                setData(updatedData);
            }
        }
    };

    const handleDeleteSubCategory = () => {
        const updatedData = { ...data };

        if (updatedData[currentSubCategory]) {
            delete updatedData[currentSubCategory];
        }

        setData(updatedData);
    };

    const handleAddNewValue = () => {
        const updatedData = {
            ...data,
            [currentSubCategory]: {
                ...data[currentSubCategory],
                [currentField]: [
                    ...(data[currentSubCategory][currentField] || []), // handle undefined
                    newValue,
                ],
            },
        };

        setData(updatedData);
        setNewValue("");
    };

    const handleAddNewRow = (subCategory: string) => {
        const input = rowInputs[subCategory];
        if (!input?.key || !input?.type) return;

        let value;
        if (input.type == "array") {
            value = [];
        } else {
            value = input.exampleText;
        }

        const updatedData = {
            ...data,
            [subCategory]: {
                ...data[subCategory],
                [input.key]: value,
            },
        };

        setData(updatedData);
        rowInputs[subCategory].key = "";
        rowInputs[subCategory].type = "null";
        rowInputs[subCategory].exampleText = "";
        setRowInputs({ ...rowInputs });
    };

    const handleSave = async () => {
        setSaveLoading(true);

        const expectedResult = {
            name,
            key,
            json: data,
        };

        await API.updateExpectedResult(id as string, expectedResult);

        setSaveLoading(false);
        naviate("/expected-results");
    };

    const handleConfirmGoback = async () => {
        naviate("/expected-results");
    };

    const loadExpectedResult = async (id: string) => {
        setLoading(true);
        const result: any = await API.getOneExpectedResult(id);
        setData(result.data.json);
        setName(result.data.name);
        setKey(result.data.key);
        setLoading(false);
    };

    useEffect(() => {
        loadExpectedResult(id as string);
    }, [id]);

    return (
        <div>
            <Container style={{ marginTop: 100 }}>
                {loading ? (
                    <div className="d-flex justify-content-center my-5">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <Row className="mb-5">
                            <Col
                                cols="6"
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 10,
                                }}
                            >
                                <a
                                    type="button"
                                    className="btn btn-outline-black btn-md mx-1"
                                    data-bs-toggle="modal"
                                    data-bs-target="#gobackAlertModal"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </a>
                                <h5 className="h5">Update Expected Result</h5>
                            </Col>
                            <Col cols="6" className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary mx-1 text-white"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addSubCategoryModal"
                                >
                                    <i className="bi bi-plus mx-2"></i>
                                    Add New Sub-Category
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary mx-1"
                                    onClick={handleSave}
                                    disabled={saveLoading}
                                >
                                    <i className="bi bi-save mx-2"></i>
                                    {saveLoading ? "Saving..." : "Save"}
                                </button>
                            </Col>
                        </Row>
                        <Row>
                            <Col cols="6">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                    />
                                    <div
                                        id="emailHelp"
                                        className="fw-lighter"
                                        style={{ fontSize: 12 }}
                                    >
                                        Specify the name for expected result
                                    </div>
                                </div>
                            </Col>
                            <Col xs="6">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Key
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={key}
                                        onChange={(e) => setKey(e.target.value)}
                                    />
                                    <div
                                        id="emailHelp"
                                        className="fw-lighter"
                                        style={{ fontSize: 12 }}
                                    >
                                        For the functionality. Only _ and
                                        lowercase letters supported.
                                    </div>
                                </div>
                            </Col>
                            {data &&
                                Object.keys(data).map((subCategory: string) => (
                                    <Col xs="12" className="mt-4">
                                        <div className="mb-3 card p-3">
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={
                                                    closestCenter
                                                }
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={Object.entries(
                                                        data[subCategory],
                                                    ).map(([key]) => key)}
                                                    strategy={
                                                        verticalListSortingStrategy
                                                    }
                                                >
                                                    <table className="table">
                                                        <tbody>
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        width: 20,
                                                                    }}
                                                                ></td>
                                                                <th>
                                                                    Sub-Category
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    style={{
                                                                        width: 300,
                                                                    }}
                                                                >
                                                                    Key
                                                                </th>
                                                                <th
                                                                    scope="col"
                                                                    style={{
                                                                        width: 500,
                                                                    }}
                                                                >
                                                                    Values
                                                                </th>
                                                                <th>
                                                                    Actions{" "}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-grey btn-sm mx-1"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#deleteModal"
                                                                        onClick={() => {
                                                                            setCurrentSubCategory(
                                                                                subCategory,
                                                                            );
                                                                        }}
                                                                        style={{
                                                                            fontSize: 12,
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-trash"></i>
                                                                    </button>
                                                                </th>
                                                            </tr>
                                                            {Object.entries(
                                                                data[
                                                                    subCategory
                                                                ],
                                                            ).map(
                                                                ([
                                                                    key,
                                                                    value,
                                                                ]) => (
                                                                    <DraggableRow
                                                                        key={
                                                                            key
                                                                        }
                                                                        id={key}
                                                                        values={{
                                                                            subCategory,
                                                                            keyName:
                                                                                key,
                                                                            value,
                                                                            handleDeleteItem,
                                                                            handleChangeKey,
                                                                            handleChangeValue,
                                                                            setCurrentSubCategory,
                                                                            setCurrentField,
                                                                            handleDeleteRow,
                                                                        }}
                                                                    />
                                                                ),
                                                            )}
                                                            {/* Optional: Input for adding new key-value */}
                                                            <tr>
                                                                <td></td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter key"
                                                                        style={{
                                                                            fontSize: 10,
                                                                        }}
                                                                        value={
                                                                            rowInputs[
                                                                                subCategory
                                                                            ]
                                                                                ?.key ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setRowInputs(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [subCategory]:
                                                                                        {
                                                                                            ...prev[
                                                                                                subCategory
                                                                                            ],
                                                                                            key: e
                                                                                                .target
                                                                                                .value,
                                                                                        },
                                                                                }),
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <select
                                                                        className="form-select"
                                                                        aria-label="Default select example"
                                                                        style={{
                                                                            fontSize: 10,
                                                                        }}
                                                                        value={
                                                                            rowInputs[
                                                                                subCategory
                                                                            ]
                                                                                ?.type ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setRowInputs(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [subCategory]:
                                                                                        {
                                                                                            ...prev[
                                                                                                subCategory
                                                                                            ],
                                                                                            type: e
                                                                                                .target
                                                                                                .value,
                                                                                        },
                                                                                }),
                                                                            )
                                                                        }
                                                                    >
                                                                        <option
                                                                            selected
                                                                            value="null"
                                                                        >
                                                                            Select
                                                                            Type
                                                                        </option>
                                                                        <option value="array">
                                                                            Array
                                                                        </option>
                                                                        <option value="input">
                                                                            Input
                                                                        </option>
                                                                    </select>
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Enter Example Text (No need for array field)"
                                                                        style={{
                                                                            fontSize: 10,
                                                                        }}
                                                                        value={
                                                                            rowInputs[
                                                                                subCategory
                                                                            ]
                                                                                ?.exampleText ||
                                                                            ""
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) =>
                                                                            setRowInputs(
                                                                                (
                                                                                    prev,
                                                                                ) => ({
                                                                                    ...prev,
                                                                                    [subCategory]:
                                                                                        {
                                                                                            ...prev[
                                                                                                subCategory
                                                                                            ],
                                                                                            exampleText:
                                                                                                e
                                                                                                    .target
                                                                                                    .value,
                                                                                        },
                                                                                }),
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary mx-1 btn-sm"
                                                                        onClick={() =>
                                                                            handleAddNewRow(
                                                                                subCategory,
                                                                            )
                                                                        }
                                                                        style={{
                                                                            fontSize: 12,
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-plus mx-2"></i>
                                                                        Add New
                                                                        Row
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </SortableContext>
                                            </DndContext>
                                        </div>
                                    </Col>
                                ))}
                        </Row>
                    </>
                )}
            </Container>

            {/* Add New Subcategory Modal */}
            <div
                className="modal fade"
                id="addSubCategoryModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                aria-labelledby="staticBackdropLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1
                                className="modal-title fs-5"
                                id="staticBackdropLabel"
                            >
                                Add New SubCategory
                            </h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <label className="form-label">
                                Sub Category Key
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="subcategory_key"
                                value={newSubCategory}
                                onChange={(e) =>
                                    setNewSubCategory(e.target.value)
                                }
                            />
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={handleAddNewSubCategory}
                            >
                                <i className="bi bi-plus mx-2"></i>
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add New Value */}
            <div
                className="modal fade"
                id="addNewValueModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                aria-labelledby="staticBackdropLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1
                                className="modal-title fs-5"
                                id="staticBackdropLabel"
                            >
                                Add New Value
                            </h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <label className="form-label">New Value</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter new value"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                            />
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={handleAddNewValue}
                            >
                                <i className="bi bi-plus mx-2"></i>
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Dialog */}
            <div
                className="modal fade"
                id="deleteModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                aria-labelledby="staticBackdropLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1
                                className="modal-title fs-5"
                                id="staticBackdropLabel"
                            >
                                Confim To Delete
                            </h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <p>
                                You are going to delete this sub category. All
                                records under this sub category will be deleted.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                onClick={handleDeleteSubCategory}
                            >
                                <i className="bi bi-trash mx-2"></i>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog alert before going back */}
            <div
                className="modal fade"
                id="gobackAlertModal"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                aria-labelledby="staticBackdropLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1
                                className="modal-title fs-5"
                                id="staticBackdropLabel"
                            >
                                Confim To Go Back
                            </h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <p>
                                If you have updated data save it first. Unsaved
                                datas will be lost if you go back.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={handleConfirmGoback}
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateExpectedResult;

function DraggableRow({
    id,
    values,
}: {
    id: string;
    values: {
        subCategory: string;
        keyName: string;
        value: any;
        handleDeleteItem: (
            subCategory: string,
            key: string,
            index: number,
        ) => void;
        handleChangeKey: (
            subCategory: string,
            oldKey: string,
            newKey: string,
        ) => void;
        handleChangeValue: (
            subCategory: string,
            key: string,
            value: string,
        ) => void;
        setCurrentSubCategory: (subCategory: string) => void;
        setCurrentField: (field: string) => void;
        handleDeleteRow: (subCategory: string, key: string) => void;
    };
}) {
    const oldKeyName = values.keyName;
    const [keyName, setKeyName] = useState(values.keyName);
    const [keyValue, setKeyValue] = useState(values.value);

    const {
        setNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id,
        data: {
            subCategory: values.subCategory,
        },
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? "#f5f5f5" : undefined,
    };

    const {
        subCategory,
        value,
        handleDeleteItem,
        handleChangeKey,
        handleChangeValue,
        setCurrentSubCategory,
        setCurrentField,
        handleDeleteRow,
    } = values;

    return (
        <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <td>
                <i
                    className="bi bi-grip-vertical mx-2"
                    style={{ fontSize: 14, opacity: 0.4 }}
                ></i>
            </td>
            <td>{subCategory}</td>
            <td>
                <input
                    style={{
                        border: "none",
                        borderRadius: 5,
                        padding: "2px 3px",
                    }}
                    type="text"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key == "Enter") {
                            handleChangeKey(subCategory, oldKeyName, keyName);
                        }
                    }}
                />
            </td>
            <td>
                {Array.isArray(value) ? (
                    value.map((v: any, idx: number) => (
                        <span
                            key={idx}
                            className="btn badge bg-secondary mx-1 my-1"
                            style={{
                                fontSize: 10,
                            }}
                            onClick={() =>
                                handleDeleteItem(subCategory, keyName, idx)
                            }
                        >
                            {v}
                            <i className="bi bi-trash mx-2"></i>
                        </span>
                    ))
                ) : (
                    <input
                        style={{
                            border: "none",
                            borderRadius: 5,
                            padding: "2px 3px",
                        }}
                        type="text"
                        value={keyValue}
                        onChange={(e) => setKeyValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key == "Enter") {
                                e.currentTarget.blur();
                                handleChangeValue(
                                    subCategory,
                                    keyName,
                                    keyValue,
                                );
                            }
                        }}
                    />
                )}
                {Array.isArray(value) && (
                    <span
                        data-bs-toggle="modal"
                        className="badge bg-primary mx-1"
                        style={{
                            fontSize: 10,
                            cursor: "pointer",
                        }}
                        data-bs-target="#addNewValueModal"
                        onClick={() => {
                            setCurrentSubCategory(subCategory);
                            setCurrentField(keyName);
                        }}
                    >
                        <i className="bi bi-plus mx-2"></i>
                        Add New
                    </span>
                )}
            </td>
            <td>
                <span
                    className="badge bg-danger primary mx-1"
                    style={{
                        fontSize: 10,
                        cursor: "pointer",
                    }}
                    onClick={() => handleDeleteRow(subCategory, keyName)}
                >
                    <i className="bi bi-trash mx-2"></i>
                    Delete
                </span>
            </td>
        </tr>
    );
}
